import $ from 'jquery';
import EventHandler from '@pixelunion/events';
import rimg from '@pixelunion/rimg-shopify';
import Forms from '../Forms';
import initShopifyProductReviews from '../helpers/ProductReviews';
import ProductGridItem from '../components/ProductGridItem';
import Modal from '../components/Modal';

function sanitizeQueryParams(params) {
  const sanitizedParams = params;

  if (typeof sanitizedParams !== 'object') {
    throw new Error('params is not of type Object');
  }

  if ('page' in sanitizedParams && sanitizedParams.page === 1) {
    delete sanitizedParams.page;
  }

  return sanitizedParams;
}

export default class StaticCollectionFaceted {
  constructor(section) {
    this.el = section.el;
    this.id = section.id;
    this.context = section.data.context;
    this.pagination = section.data.pagination;
    this.processing = false;
    this.timeout = null;
    this.initializedProductCards = [];
    this.events = new EventHandler();
    this.modal = new Modal();
    this.forms = new Forms($(this.el));
    this.queryParams = {
      page: 1,
      grid_list: 'grid-view',
    };
    // Each time we replace page content we'll need to unload and re-bind
    // events. Save the selecters instead of the elements so we don't need to
    // update class variables each time
    this.filterContentSelector = '[data-productgrid-filters-content]';
    this.filterInputSelector = '[data-filter-checkbox]';
    this.filterFormSelector = '[data-filter-form]';
    this.filterModalSelector = '[data-productgrid-trigger-filters]';
    this.filterRemoveSelector = '[data-filter-remove]';
    this.filterToggleSelector = '[data-filter-toggle]';
    this.paginationWrapperSelector = '[data-pagination-wrapper]';
    this.productGridSelector = '[data-product-grid]';
    this.productItemSelector = '[data-product-item]';
    this.rangeInputSelector = '[data-filter-range]';
    this.responseContainerSelector = '[data-results-container]';
    this.showBySelector = '[data-show-by]';
    this.sidebarContainerSelector = '[data-product-grid-sidebar]';
    this.sortContentSelector = '[data-productgrid-sort-content]';
    this.sortByModalSelector = '[data-productgrid-modal-sort]';
    this.sortBySelector = '[data-productgrid-trigger-sort]';
    this.sortByButtonSelector = '[data-productgrid-trigger-sort-button]';
    this.viewToggleSelector = '[data-collection-view]';

    const productCards = this.el.querySelectorAll(this.productItemSelector);
    this._initProductCards(productCards);
    this._getQueryParams();
    this._toggleView(this.queryParams.grid_list);
    this._bindEvents();
  }

  _bindEvents() {
    const filterInputs = this.el.querySelectorAll(this.filterInputSelector);
    const filterModalTrigger = this.el.querySelector(this.filterModalSelector);
    const filterRemoveLinks = this.el.querySelectorAll(this.filterRemoveSelector);
    const filterToggle = this.el.querySelectorAll(this.filterToggleSelector);
    const rangeInputs = this.el.querySelectorAll(this.rangeInputSelector);
    const sortButtons = document.querySelectorAll(this.sortByButtonSelector);
    const sortModalTrigger = this.el.querySelector(this.sortByModalSelector);
    const sortSelect = this.el.querySelector(this.sortBySelector);
    const viewLinks = this.el.querySelectorAll(this.viewToggleSelector);
    let paginationTrigger = null;

    // Filter events
    filterInputs.forEach(input => {
      this.events.register(input, 'change', e => {
        if (this.processing) return;
        this._handleFilterClick(e.currentTarget);
      });
    });

    rangeInputs.forEach(input => {
      this.events.register(input, 'change', e => this._handleFilterRange(e.currentTarget));
      this.events.register(input, 'keyup', e => {
        if (e.key !== 'Tab') {
          this._handleFilterRange(e.currentTarget);
        }
      });
    });

    filterRemoveLinks.forEach(link => {
      this.events.register(link, 'click', e => {
        e.preventDefault();
        const target = e.currentTarget;
        const href = target.href.indexOf('?') === -1 ? '' : target.href.slice(target.href.indexOf('?') + 1);

        this._fetchResults(false, href);
      });
    });

    filterToggle.forEach(toggle => {
      this.events.register(toggle, 'click', e => {
        e.preventDefault();
        this._toggleFilters(e.currentTarget);
      });
    });

    // Modal triggers
    this.events.register(filterModalTrigger, 'click', () => this.modal.open(this.filterContentSelector, 'productgrid-filters'));
    this.events.register(sortModalTrigger, 'click', () => this.modal.open(this.sortContentSelector, 'productgrid-sort'));

    // Grid view/List view events
    viewLinks.forEach(link => {
      this.events.register(link, 'click', e => {
        e.preventDefault();
        if (this.processing) return;
        this._handleViewToggle(e.currentTarget);
      });
    });

    // Sorting events
    this.events.register(sortSelect, 'change', () => this._handleSort());
    sortButtons.forEach(button => {
      this.events.register(button, 'click', e => this._handleSort(e.currentTarget));
    });

    // Pagination events
    if (this.pagination === 'click_to_load') {
      paginationTrigger = this.el.querySelector('[data-click-to-load]');

      this.events.register(paginationTrigger, 'click', e => this._handlePagination(e.currentTarget));
    } else if (this.pagination === 'infinite_scroll') {
      paginationTrigger = this.el.querySelector('[data-infinite-scroll]');

      if (paginationTrigger) {
        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.intersectionRatio > 0) {
              this._handlePagination(paginationTrigger);
            }
          });
        });

        observer.observe(paginationTrigger);
      }
    } else { // Traditional pagination
      paginationTrigger = this.el.querySelectorAll('a.pagination__item');

      paginationTrigger.forEach(el => {
        this.events.register(el, 'click', e => this._handlePagination(e.currentTarget));
      });
    }
  }

  _unbindEvents() {
    this.events.unregisterAll();
  }

  _initProductCards(productCards) {
    const productCardArray = Array.from(productCards);

    const initializedCards = productCardArray.map(card => {
      const productData = JSON.parse(card.querySelector('[data-product-data]').innerHTML);
      return new ProductGridItem({
        $el: $(card),
        id: this.id,
        product: productData,
        queryParams: this.queryParams,
        lazy: true,
      });
    });

    this.initializedProductCards = this.initializedProductCards.concat(initializedCards);

    if (this.el.querySelector('.spr-badge')) initShopifyProductReviews();
  }

  _getQueryParams() {
    if (window.location.search.length > 0) {
      const splitQueryString = window.location.search.replace('?', '').split('&');

      splitQueryString.forEach(query => {
        const queryKey = query.split('=')[0];
        const queryValue = query.split('=')[1];
        // Only save non-filter queries (ie. page, view, sort, grid_list, vendor)
        if (queryKey === 'page' || queryKey === 'grid_list' || queryKey === 'sort_by' || queryKey === 'q') {
          this.queryParams[queryKey] = queryValue;
        }
      });
    }

    this._handleViewToggle(null);
  }

  /*
   * Get filter params from form data
   */
  _getFilterParams() {
    const filterForm = document.querySelector(this.filterFormSelector);
    let searchString = '';

    if (filterForm) {
      const formData = new FormData(filterForm);
      searchString = new URLSearchParams(formData).toString();
    }

    return searchString;
  }

  /*
   * Filter checkbox handler
   */
  _handleFilterClick(targetEl) {
    const filterItem = targetEl.closest('[data-filter-item]');

    filterItem.classList.toggle('filter-item--active');
    filterItem.classList.toggle('filter-item--inactive');

    if (this.timeout !== null) {
      clearTimeout(this.timeout);
    }

    // Wait for user to finish entering input before reloading page
    this.timeout = setTimeout(() => {
      this._fetchResults();
    }, 1000);
  }

  _handleFilterRange() {
    if (this.timeout !== null) {
      clearTimeout(this.timeout);
    }

    // Wait for user to finish entering input before reloading page
    this.timeout = setTimeout(() => {
      this._fetchResults();
    }, 2000);
  }

  _toggleFilters(toggleEl) {
    if (this.processing) return;
    const siblingsVisible = toggleEl.getAttribute('data-filter-toggle') === 'true';
    const text = siblingsVisible
      ? this.context.see_more
      : this.context.see_less;

    const hiddenFilters = toggleEl
      .closest('.filter-group')
      .querySelectorAll('[data-hidden-default]');

    hiddenFilters.forEach(filter => {
      if (siblingsVisible) {
        filter.classList.add('filter-item--hidden');
      } else {
        filter.classList.remove('filter-item--hidden');
      }
    });

    toggleEl.innerText = text;
    toggleEl.dataset.filterToggle = !siblingsVisible;

    if (this.modal.isOpen()) {
      this.modal.position();
    }
  }

  /*
   * Toggle active class on view-by (grid or list view) buttons
   */
  _toggleView(view = 'grid-view') {
    if (this.el.querySelector(this.viewToggleSelector)) {
      this.el.querySelectorAll(this.viewToggleSelector)
        .forEach(el => {
          el.classList.remove('active');
        });

      this.el.querySelector(`[data-collection-view="${view}"]`).classList.add('active');
    }

    const className = view.replace('-', '');
    const gridContainer = this.el.querySelector('.productgrid--outer');

    if (className === 'listview') {
      gridContainer.classList.remove('productgrid-gridview');
    } else {
      gridContainer.classList.remove('productgrid-listview');
    }

    gridContainer.classList.add(`productgrid-${className}`);
  }

  /*
   * Grid/list view click handler
   */
  _handleViewToggle(targetEl = null) {
    let view = this.queryParams.grid_list;

    if (targetEl) {
      view = targetEl.getAttribute('data-collection-view');
      this.queryParams.grid_list = view;
    }

    this._toggleView(view);

    if (this.initializedProductCards) {
      this.initializedProductCards.forEach(card => card.handleViewChange(view));
    }

    // Update history only if the grid/list view toggle is enabled
    if (this.el.querySelector(this.viewToggleSelector)) {
      this._updateHistory();
    }
  }

  /*
   * Set sort value in stored queryParams
   */
  _setSortValue(targetButton = null) {
    let sortValue = ''
    if (targetButton) {
      sortValue = targetButton.getAttribute('value');
    } else {
      sortValue = this.el.querySelector(this.sortBySelector).value;
    }

    this.queryParams.sort_by = sortValue;
  }

  /*
   * Sort by event handler
   */
  _handleSort(targetButton = null) {
    if (this.processing) return;
    const activeClass = 'utils-sortby--modal-button--active';

    if (targetButton) {
      // Toggle active class and disabled attribute
      const activeButton = document.querySelector(`.${activeClass}`);
      activeButton.classList.remove(activeClass);
      activeButton.removeAttribute('disabled');

      targetButton.classList.add(activeClass);
      targetButton.setAttribute('disabled', true);
    }

    this._setSortValue(targetButton);
    this._fetchResults();
  }

  /*
   * Pagination event handler
   */
  _handlePagination(trigger) {
    const toPage = trigger.getAttribute('data-to-page');
    this.queryParams.page = parseInt(toPage, 10);
    this._fetchResults(true);
  }

  /*
   * Set processing variable while fetching
   * and update processing classes
   */
  _setProcessing(processing = true, type = 'filter') {
    this.processing = processing;
    const sidebarContainer = this.el.querySelector(this.sidebarContainerSelector);
    const responseContainer = this.el.querySelector(this.responseContainerSelector);

    if (processing) {
      if (type === 'filter') {
        const y = $(window).scrollTop();
        if (y >= 310) {
          $('html, body')
            .animate({ scrollTop: y - 300 }, 150)
            .animate({ scrollTop: 10 }, 0)
            .animate({ scrollTop: 0 }, 150);
        } else {
          $('html, body').animate({ scrollTop: 0 }, 150);
        }
      }

      if (sidebarContainer) {
        sidebarContainer.classList.add(`processing--${type}`);
      }
      responseContainer.classList.add(`processing--${type}`);
    } else {
      if (sidebarContainer) {
        sidebarContainer.classList.remove(`processing--${type}`);
      }
      responseContainer.classList.remove(`processing--${type}`);
    }
  }

  /*
   * Click to load and infinite scroll need to append results instead of replace
   */
  _appendResults(html) {
    const productGrid = this.el.querySelector(this.productGridSelector);
    const paginationContainer = this.el.querySelector(this.paginationWrapperSelector);
    const responseContainer = this.el.querySelector(this.responseContainerSelector);
    const fragment = document.createDocumentFragment();
    const productItems = document.createElement('div');

    productItems.innerHTML = html.querySelector(this.productGridSelector).innerHTML;

    while (productItems.firstChild) {
      fragment.appendChild(productItems.firstChild);
    }

    const newProductItems = fragment.querySelectorAll(this.productItemSelector);
    productGrid.appendChild(fragment);
    this._initProductCards(newProductItems);

    if (paginationContainer) {
      paginationContainer.innerHTML = html.querySelector(this.paginationWrapperSelector).innerHTML;
    }

    rimg.watch(responseContainer);
  }

  _replaceResults(html) {
    const responseContainer = this.el.querySelector(this.responseContainerSelector);
    const sidebarContainer = this.el.querySelector(this.sidebarContainerSelector);
    this.initializedProductCards.forEach(item => item.unload());
    this.initializedProductCards = [];
    this._unbindEvents();

    responseContainer.innerHTML = html.querySelector(this.responseContainerSelector).innerHTML;

    this._initProductCards(responseContainer.querySelectorAll(this.productItemSelector));

    if (sidebarContainer) {
      sidebarContainer.innerHTML = html.querySelector(this.sidebarContainerSelector).innerHTML;
    }

    rimg.watch(responseContainer);
  }

  _fetchResults(append = false, filterParams = null) {
    const processingType = append ? 'pagination' : 'filter';
    const urlPath = window.location.pathname;
    const filterQuery = filterParams === null ? this._getFilterParams() : filterParams;

    // If we are replacing page contents instead of appending via infinite scroll/click-to-load
    // it means we are filtering or sorting and should return to page 1
    if (!append) {
      this.queryParams.page = 1;
    }

    const queryString = this._stringifyQueryParams(this.queryParams);
    const url = `${urlPath}?${queryString}&${filterQuery}`;
    this._setProcessing(true, processingType);
    this._unbindEvents();

    fetch(url)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        if (append) {
          this._appendResults(doc.body);
        } else {
          this._replaceResults(doc.body);
        }

        this._updateHistory();
        this._bindEvents();
        this._handleViewToggle(null);
        this._setProcessing(false, processingType);
        if (this.modal.isOpen()) {
          this.modal.close();
        }
      });
  }

  _updateHistory() {
    if ((window.Shopify && window.Shopify.designMode) || !window.history.pushState) {
      return;
    }

    const queryString = this._stringifyQueryParams(this.queryParams);
    const filterQueryString = this._getFilterParams();

    let href = `${window.location.pathname}?${queryString}`;

    if (filterQueryString) {
      href = `${href}&${filterQueryString}`;
    }

    if (window.location.href !== href) {
      window.history.pushState({}, '', href);
    }
  }

  _stringifyQueryParams(params) {
    const query = Object.keys(sanitizeQueryParams(params)).reduce(
      (queryArray, currentKey) => {
        queryArray.push(`${currentKey}=${this.queryParams[currentKey]}`);
        return queryArray;
      },
      [],
    );

    const queryString = query.join('&');
    return queryString;
  }

  onSectionUnload() {
    this._unbindEvents();

    this.modal.unload();

    this.initializedProductCards.forEach(card => {
      card.unload();
    });
  }
}
